/**
 * A bench for the floorplan renderer.
 *
 * Every case below goes through the **real** `renderWorld`, with the same
 * document model the editor uses, so nothing here can drift from what the plan
 * actually draws. It exists because the interesting bugs in this renderer are
 * all invisible to unit tests and to code review — a selection box beside the
 * symbol it describes, an empty angle where two walls meet, a symbol scaled
 * from the wrong axis — and are obvious the moment they are put side by side.
 *
 * Each case draws its geometry *and* the outline the editor would put on it
 * when selected, from the same `equipmentFootprintCorners` the editor calls.
 * That pairing is the point: if the two ever disagree again, this page shows it.
 */
import { useMemo, useState } from 'react'
import { useAssetTypes } from '../../hooks/useQueries'
import { defaultLayerStates, equipmentFootprintCorners, renderWorld, type LayerStates } from './engine/render'
import { IDENTITY } from './engine/geometry'
import type { EditorElement, PlanSymbol, SceneDoc } from './engine/scene'
import './plan-editor.css'

interface Case {
    title: string
    note: string
    /** Millimetres across, for the viewBox. */
    span: number
    elements: EditorElement[]
    symbols?: Record<string, PlanSymbol>
}

let seq = 0
const key = (): string => `lab-${++seq}`

const wall = (x1: number, y1: number, x2: number, y2: number, thickness = 200): EditorElement => ({
    key: key(),
    kind: 'wall',
    source: { transform: IDENTITY, localId: key() },
    owner: {},
    start: { x: x1, y: y1 },
    end: { x: x2, y: y2 },
    thickness,
})

const equipment = (
    typeCode: string | undefined,
    position: { x: number; y: number },
    width: number,
    depth: number,
    rotationDeg = 0,
): EditorElement => ({
    key: key(),
    kind: 'equipment',
    source: { transform: IDENTITY, localId: key() },
    owner: {},
    typeCode,
    position,
    width,
    depth,
    rotationDeg,
})

const doc = (elements: EditorElement[], symbols: Record<string, PlanSymbol> = {}): SceneDoc => ({
    frame: { uid: 'lab', name: 'Lab', typeCode: 'floor' },
    elements,
    underlays: [],
    symbols,
    repTitles: {},
    registrationEdits: {},
} as unknown as SceneDoc)

/** One case, rendered through the real pipeline into its own little stage. */
function Stage({ item, layers }: { item: Case; layers: LayerStates }) {
    const scene = doc(item.elements, item.symbols ?? {})
    const markup = renderWorld(scene, {
        layers,
        scale: 300 / item.span,
        underlayHref: () => '',
        selectedKey: null,
    })
    const outlines = item.elements
        .filter((el): el is Extract<EditorElement, { kind: 'equipment' }> => el.kind === 'equipment')
        .map((el) => equipmentFootprintCorners(el, el.typeCode ? scene.symbols[el.typeCode] : undefined))

    const half = item.span / 2
    return (
        <figure className="m-0 flex flex-col gap-1" data-testid={`plan-lab-case-${item.title}`}>
            <svg
                viewBox={`${-half} ${-half} ${item.span} ${item.span}`}
                className="plan-editor-stage h-52 w-full rounded border border-border"
                role="img"
                aria-label={item.title}
            >
                <g dangerouslySetInnerHTML={{ __html: Object.values(markup).join('') }} />
                {outlines.map((corners, i) => (
                    <polygon
                        key={i}
                        points={corners.map((p) => `${p.x},${p.y}`).join(' ')}
                        fill="none"
                        stroke="var(--primary)"
                        strokeWidth={item.span / 200}
                        data-testid="plan-lab-outline"
                    />
                ))}
            </svg>
            <figcaption className="text-xs">
                <span className="font-medium text-foreground">{item.title}</span>
                <span className="block text-[11px] text-muted-foreground">{item.note}</span>
            </figcaption>
        </figure>
    )
}

export function PlanRenderingLab({ org }: { org: string }) {
    const { data: assetTypes = [] } = useAssetTypes(org)
    const [layers] = useState<LayerStates>(() => defaultLayerStates())

    // The organization's real symbols, so this page shows what this
    // organization's plans will actually look like.
    const symbols = useMemo(() => {
        const out: Record<string, PlanSymbol> = {}
        for (const type of assetTypes as unknown as { code?: string; properties?: Record<string, unknown> }[]) {
            const symbol = type.properties?.plan_symbol as PlanSymbol | undefined
            if (type.code && symbol?.svg) out[type.code] = symbol
        }
        return out
    }, [assetTypes])

    const joins: Case[] = [
        {
            title: 'Right angle',
            note: 'Butt caps leave an empty angle; the junction fill mitres it.',
            span: 3000,
            elements: [wall(-1200, 0, 0, 0), wall(0, 0, 0, -1200)],
        },
        {
            title: 'Acute angle',
            note: 'A sharp corner mitres too, until the miter limit turns it into a bevel.',
            span: 3000,
            elements: [wall(-1200, 400, 0, 0), wall(0, 0, -1200, -400)],
        },
        {
            title: 'T junction',
            note: 'Three arms: the smallest convex cap over all of them.',
            span: 3000,
            elements: [wall(-1200, 0, 0, 0), wall(0, 0, 1200, 0), wall(0, 0, 0, 1200)],
        },
        {
            title: 'Unequal thickness',
            note: 'The fill spans both half-widths, so the thin wall does not notch the thick one.',
            span: 3000,
            elements: [wall(-1200, 0, 0, 0, 400), wall(0, 0, 0, -1200, 100)],
        },
        {
            title: 'Free end',
            note: 'Nothing meets here, so nothing is added: the wall ends where it was drawn.',
            span: 3000,
            elements: [wall(-800, 0, 800, 0)],
        },
    ]

    const symbolCases: Case[] = Object.keys(symbols)
        .filter((code) => !code.startsWith('openings_'))
        .sort()
        .flatMap((code): Case[] => {
            const symbol = symbols[code]
            const width = symbol.width_mm ?? 600
            const depth = symbol.depth_mm ?? 300
            const span = Math.max(width, depth) * 2.2
            return [{
                title: code,
                note: `${width} × ${depth} mm · the outline is the editor's own footprint`,
                span,
                symbols,
                elements: [equipment(code, { x: 0, y: 0 }, width, depth)],
            }, {
                title: `${code} @45°`,
                note: 'Rotation turns the footprint about the anchor, outline included.',
                span,
                symbols,
                elements: [equipment(code, { x: 0, y: 0 }, width, depth, 45)],
            }]
        })

    return (
        <div className="flex flex-col gap-6" data-testid="plan-rendering-lab">
            <section>
                <h2 className="pb-1 text-sm font-semibold text-foreground">Wall junctions</h2>
                <p className="pb-3 text-xs text-muted-foreground">
                    Every corner below is drawn by the same code the editor uses. An empty angle
                    here is an empty angle on every plan.
                </p>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {joins.map((item) => <Stage key={item.title} item={item} layers={layers} />)}
                </div>
            </section>

            <section>
                <h2 className="pb-1 text-sm font-semibold text-foreground">
                    Equipment symbols ({symbolCases.length / 2})
                </h2>
                <p className="pb-3 text-xs text-muted-foreground">
                    Each symbol with the selection outline the editor would put on it. The two must
                    coincide: a symbol is placed by its <em>anchor</em>, and an anchor that is not
                    the middle of the viewBox used to leave the box floating beside the shape.
                </p>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {symbolCases.map((item) => <Stage key={item.title} item={item} layers={layers} />)}
                </div>
            </section>
        </div>
    )
}
